/**
 * A field-by-field form.
 *
 * One question on screen at a time, a Next button, Enter to continue, Escape to
 * go back. The shape most people know from Typeform, and right here for the same
 * reason it works there: the forms that matter on this platform are asked once,
 * of somebody deciding whether to trust us with their company, and a wall of
 * fourteen inputs is the least reassuring thing to put in front of them.
 *
 * These types are separate from the components so a server component can
 * declare the steps — including any value derived on the server, such as the
 * country detected from the request — and hand them to the client engine.
 *
 * **The server action stays the source of truth.** Every `validate` here is
 * there to answer immediately rather than to be trusted; the action re-parses
 * everything with Zod, and a step that passed in the browser can still be
 * rejected on submit.
 */

import type { ReactNode } from 'react';

export type FlowValues = Record<string, string>;

/**
 * A piece of a step's copy, which may depend on what has been answered.
 *
 * Read through `stepQuestion` and `stepHelp` rather than unwrapped where it is
 * used — see the note on `question` for what the single reader is protecting.
 */
export type FlowText = string | ((values: FlowValues) => string);

/**
 * Work a step does on its way out, which answers other questions for you.
 *
 * The Steam import is what this is for, and it is the reason a flow can have
 * one at all: most studios arriving at the submission form already have a Steam
 * page, and everything on it except the commercial terms is already written
 * down. Pasting a link beats typing a name a second time.
 *
 * **It was a button beside the field**, labelled `Fill from Steam`, and the
 * button was the mistake. It put two controls under one answer — one that reads
 * the page and one that moves on — with no way to tell from looking which was
 * the one to press, and pressing the wrong one carried an empty name and an
 * empty price into the next three questions. Nothing about the link step wants
 * that choice: there is exactly one thing to do with a Steam link here, and
 * Next is already the control that means *I am done with this answer*.
 *
 * So it runs on the way past. The field is required like any other, so Next is
 * dead until a link is in it; pressing Next reads the page, writes the answers
 * and continues in one act, and a link Steam will not give us stops the step
 * with the reason under the field, where a wrong link is fixed.
 *
 * **It fills answers in and submits nothing.** What comes back is written into
 * the flow's values as though it had been typed, every step it touched is still
 * editable, and the review screen at the end is still what sends anything.
 */
export interface FlowAction {
  /** On the Next button while it runs. Name the act — "Reading Steam". */
  busyLabel: string;
  /**
   * Answers to write, keyed by step name. Throw to report a problem: the
   * message is shown under the field, where a wrong link is fixed.
   */
  run: (value: string) => Promise<FlowValues>;
}

/** How a step's answer is turned into the value the server action receives. */
export type FlowSubmitTransform = (value: string, values: FlowValues) => string;

interface FlowFieldBase {
  /** Form field name. Must match what the server action reads. */
  name: string;
  /**
   * Whether this question applies at all, given the answers so far.
   *
   * A flow is a fixed list of steps and most questions are unconditional, but
   * some only exist because of an earlier answer — the Steam link is worth
   * asking for only once somebody has said they have a Steam page, and asking
   * anyway would be a screen whose honest answer is "I already told you no".
   *
   * A step that does not apply is not counted in the progress strip, is not
   * shown on the review screen, and submits an empty value whatever was typed
   * into it before the answer above it changed.
   */
  when?: (values: FlowValues) => boolean;
  /**
   * The question, set large. One sentence, in the second person.
   *
   * **A string, or a function of the answers so far, and the string is still
   * the right answer nearly everywhere.** This used to be a string only, and
   * `onboarding.tsx` argued for it in the note beside `personName`: four places
   * print a question — the heading, the review row, the progress strip's
   * tooltip and the `aria-label` on every bar — so a question computed from an
   * answer had four places to be recomputed and one of them would be missed.
   *
   * That was the correct objection to make and it is answered rather than
   * overruled: all four now read through `stepQuestion`, which is the only
   * thing in this package that unwraps the value. There is one place to be
   * wrong, and it is a two-line function.
   *
   * **When to reach for the function.** Only when the variants cannot be
   * enumerated as steps. Two audiences is two steps with `when` — that is what
   * the name and the country questions do, and it reads better on the review
   * screen than one question that quietly rewrote itself. Forty-one countries
   * is not two steps: `What is the company number?` is the wrong question in
   * most of the countries this product onboards from, and the right one is
   * `What is the KRS number?` (#101). See `companyNumberQuestion` in
   * `apps/api/src/lib/company-registers`.
   *
   * It is called during render and on every keystroke of the step above it, so
   * it has to be cheap and it has to be pure — a lookup in a table, not a fetch.
   */
  question: FlowText;
  /** A sentence under the question. Context, never instructions. */
  help?: FlowText;
  /**
   * Live feedback under the field — the resulting URL, the formatted price.
   *
   * Section 06: show the consequence of an answer while it is being given,
   * rather than after submitting.
   */
  hint?: (value: string, values: FlowValues) => string | null;
  /** Whether the step can be passed without an answer. */
  optional?: boolean;
  /** The wording on the skip control. "I don't have one" beats "Skip". */
  skipLabel?: string;
  /** Immediate, client-side. Returns a message, or null when the value is fine. */
  validate?: (value: string, values: FlowValues) => string | null;
  /**
   * A first answer computed from earlier ones, so the step is an acceptance
   * rather than a blank field.
   *
   * Recomputed whenever the step is reached with an untouched value, so going
   * back and changing the studio name updates the address that was generated
   * from it — but never overwrites an address that was typed deliberately.
   */
  derive?: (values: FlowValues) => string;
  /**
   * A uniqueness check against the database, by endpoint name.
   *
   * Every value here has to be a kind the resolver passed as `checkAvailability`
   * actually understands. A typo is silent rather than loud: an endpoint that
   * does not recognise the kind answers 404, `runCheck` reads any failure as
   * *no objection*, and the step simply stops checking without saying so.
   *
   * Both are branches of `GET /v1/availability/:kind`.
   *
   * **There were four** (#144). `studio-slug` asked whether
   * `goodbuild.cc/dev/<slug>` was free, for the studio registration; and
   * `developer-handle` asked about a person's professional profile, through
   * `GET /v1/developer-profiles/available` rather than the availability
   * endpoint, because a developer handle and a creator handle were separate
   * namespaces. Neither address exists any more.
   */
  check?: 'product-slug' | 'creator-handle';
  /**
   * A check against something outside this product entirely.
   *
   * `check` asks our own database whether an address is free. This asks a third
   * party whether an answer is true — today, a country's company register
   * whether the number somebody typed belongs to a company (#102). The two are
   * separate because the policies differ in the direction that matters: a slug
   * collision is our fact and we are certain of it, and a foreign register's
   * silence is not evidence of anything.
   *
   * Same silent-failure hazard as `check`, and worse odds of hitting it: a kind
   * the resolver does not recognise, or a `<Flow>` handed no `verifyAnswer` at
   * all, does not break — the step simply stops verifying and nothing on the
   * screen says so. `onboarding-flows.test.ts` sweeps for both.
   *
   * `context` is how the resolver learns what else it needs. A company number
   * means nothing without the country whose register issues it, and that was
   * answered three steps earlier — so the step names what to send rather than
   * the resolver reaching into `values` for a key it would have to guess.
   */
  verify?: {
    kind: 'company-number';
    context?: (values: FlowValues) => Record<string, string>;
  };
  /** Convert to the value the action expects — euros to cents, for instance. */
  submit?: FlowSubmitTransform;
  /** How the answer reads on the review screen, when the raw value would not. */
  display?: (value: string, values: FlowValues) => string;
  /**
   * Something to show under the field that a sentence cannot carry.
   *
   * `hint` is the usual answer and returns a string. This is for the case that
   * is not text: the Steam import puts the game's header image here, because a
   * picture of the wrong game is the fastest way to see that the wrong link was
   * pasted, which is the failure that feature actually has.
   */
  preview?: (values: FlowValues) => ReactNode;
}

export interface FlowTextStep extends FlowFieldBase {
  kind: 'text';
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  /** Rendered inside the field, before the input. `goodbuild.cc/dev/`. */
  prefix?: string;
  autoCapitalize?: 'none' | 'words';
  /** Work done on the way out that answers other questions. See `FlowAction`. */
  action?: FlowAction;
}

export interface FlowChoiceOption {
  value: string;
  label: string;
  /** Sorts and heads a group in the list. */
  group?: string;
}

export interface FlowChoiceStep extends FlowFieldBase {
  kind: 'choice';
  options: readonly FlowChoiceOption[];
  /** Placeholder for the filter box. */
  searchPlaceholder?: string;
  /**
   * What the empty filter says when nothing matches.
   *
   * **It was written into the renderer**, as `Nothing matches. GOODBUILD can
   * only onboard from the countries listed.` — which is a true and useful
   * sentence about the one list this kind has ever held, and a lie about every
   * other list somebody might reach for it with. A filtered list is a generic
   * control; the reason a filter came up empty is not.
   *
   * It is also the sentence that does the most work on the country steps, and
   * the honest wording differs between them: the server refuses an unlisted
   * country with `GOODBUILD cannot onboard a studio from that country yet` on
   * one endpoint and `an organisation` on another, and an empty state that
   * echoes the refusal somebody would have got is better than one that
   * paraphrases all of them at once.
   *
   * The old string is the default, so an unset list keeps exactly the empty
   * state it had.
   */
  emptyHint?: string;
}

export interface FlowCardOption {
  value: string;
  label: string;
  description?: string;
}

export interface FlowCardsStep extends FlowFieldBase {
  kind: 'cards';
  options: readonly FlowCardOption[];
  /**
   * Stay on the question after a card is picked, instead of moving on.
   *
   * A card is a decision rather than a draft, so picking one continues — that
   * is the whole reason this kind exists beside `choice`, and it is right for
   * every question whose answer is the last thing there is to know about it.
   *
   * It is wrong for one: the creator rate. What that answer *means* is four
   * figures — what the creator gets, what the studio gets, what GOODBUILD gets,
   * and what the tax and the processing took first — and they can only be
   * worked out once the rate is known. So the screen that shows them is the
   * screen that has just been left. A studio picked a number, the flow jumped,
   * and the arithmetic they were choosing between appeared and disappeared in
   * the same instant.
   *
   * Set here rather than inferred from the presence of a `preview`, because
   * plenty of steps have something under the field that is worth a glance and
   * not worth stopping for — the Steam import's header image is read on the way
   * *back*, not on the way through.
   */
  stay?: boolean;
}

/**
 * An answer that is obtained rather than typed.
 *
 * The creator application is what this exists for (#91): the first thing it
 * asks is that somebody prove they own the channel they are claiming, and
 * proof is an OAuth round trip through the platform rather than a box. Every
 * other kind here takes something a person can assert; this one takes something
 * only the platform can say.
 *
 * **The connect runs from the step's primary button, not from a second one
 * beside the field**, which is the same decision `FlowAction` records and for
 * the same reason: two controls under one answer, with nothing to say which is
 * which, is how somebody carries an empty answer into the next three questions.
 * There is exactly one thing to do on this screen, so there is one button, and
 * it says what it does — `Connect Twitch` rather than `Next`. Once the answer
 * is in, the button goes back to being Next and a quieter control offers to do
 * it again.
 *
 * **It writes several answers at once**, exactly like `FlowAction`: a
 * verification comes back with the handle, the display name and the channel
 * address, and none of those is a question any more (#94). What it returns is
 * written into the flow's values as though it had been typed, and every step it
 * touches is marked as answered so a `derive` will not overwrite it.
 *
 * **It is not a substitute for the server checking.** What the browser gets
 * back is a receipt that a verification happened; the endpoint reads the
 * verification itself, keyed to the caller, and takes the handle from there. A
 * client that posted its own handle would be asserting exactly the thing this
 * step exists to stop being asserted.
 */
export interface FlowConnectStep extends FlowFieldBase {
  kind: 'connect';
  /**
   * On the primary button while the answer is missing. Name the act, and name
   * who it is with — `Connect Twitch`, not `Connect`.
   *
   * A function of the answers because the target is usually chosen a step
   * earlier: which platform, which provider, which account.
   */
  connectLabel: (values: FlowValues) => string;
  /** On the same button while it runs. `Waiting for Twitch`. */
  busyLabel: (values: FlowValues) => string;
  /** The quieter control offered once it has worked. */
  againLabel?: string;
  /**
   * Do it. Resolves with the answers to write, which must include this step's
   * own `name` — an empty value there reads as *not done* and the flow will
   * not move on.
   *
   * Throw to report a problem: the message is shown under the question, where
   * the button that failed is.
   */
  connect: (values: FlowValues) => Promise<FlowValues>;
}

export interface FlowNumberStep extends FlowFieldBase {
  kind: 'number';
  min?: number;
  max?: number;
  step?: number;
  /** Rendered inside the field before the number. */
  prefix?: string;
  /** Rendered after it. */
  suffix?: string;
}

export type FlowStep =
  | FlowTextStep
  | FlowChoiceStep
  | FlowCardsStep
  | FlowConnectStep
  | FlowNumberStep;

/** The value a step starts with, before anybody touches it. */
export function initialValues(steps: readonly FlowStep[]): FlowValues {
  const values: FlowValues = {};
  for (const step of steps) values[step.name] = '';
  return values;
}

/**
 * The steps that apply, given what has been answered.
 *
 * Everything in the engine counts through this rather than through `steps`:
 * the progress strip, the step index, the review list and what is submitted.
 * A step that stopped applying halfway through must not still be occupying a
 * position, or Back walks into a question that is no longer being asked.
 */
export function activeSteps(steps: readonly FlowStep[], values: FlowValues): readonly FlowStep[] {
  return steps.filter((step) => step.when?.(values) ?? true);
}

/**
 * What the server action receives, after every `submit` transform.
 *
 * A step that no longer applies submits nothing, whatever is still sitting in
 * its value. Somebody who pasted a Steam link, went back and said they have no
 * Steam page has withdrawn the link — leaving it in the payload because the
 * string is still in state would send an answer they took back.
 */
export function submitValues(steps: readonly FlowStep[], values: FlowValues): FlowValues {
  const active = new Set(activeSteps(steps, values).map((step) => step.name));
  const out: FlowValues = {};

  for (const step of steps) {
    const raw = active.has(step.name) ? (values[step.name] ?? '') : '';
    out[step.name] = step.submit ? step.submit(raw, values) : raw;
  }

  return out;
}

/**
 * The question a step is asking, given what has been answered.
 *
 * **The only place a `FlowText` is unwrapped**, which is the whole of the
 * argument for letting one be a function at all. Four places print a question
 * and every one of them goes through here: the heading, the review row, the
 * progress strip's tooltip and its `aria-label`. A fifth that read
 * `step.question` directly would typecheck as `string | Function` and render
 * the source of a function into the page, which is loud — but only on the
 * variant that made it one.
 */
export function stepQuestion(step: FlowStep, values: FlowValues): string {
  return typeof step.question === 'function' ? step.question(values) : step.question;
}

/** The sentence under it, or null when the step has none. */
export function stepHelp(step: FlowStep, values: FlowValues): string | null {
  if (step.help === undefined) return null;
  const help = typeof step.help === 'function' ? step.help(values) : step.help;
  return help === '' ? null : help;
}

/** The first problem with a step's answer, or null when there is none. */
export function stepError(step: FlowStep, value: string, values: FlowValues): string | null {
  const answer = value.trim();

  if (answer === '') {
    if (step.optional) return null;
    return 'This one is needed to continue.';
  }

  return step.validate?.(answer, values) ?? null;
}
